"use client"

import { useQuery } from "@tanstack/react-query"
import debounce from "lodash/debounce"
import { useRouter, useParams } from "next/navigation"
import React, { useState, useCallback, useRef, useEffect } from "react"

import { SearchItemsParams } from "@lib/clients/nelo-api-client"
import { neloApiClient } from "@lib/config-client"
import { buildRoute } from "@lib/routes"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

import SearchResults from "../SearchResults"
import { BinocularsIcon } from "./BinocularsIcon"

const WebSearchBar: React.FC = () => {
  const { countryCode } = useParams<{ countryCode: string; categorias: string[] }>()
  const [, setIsFocused] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [showResults, setShowResults] = useState(false)
  const searchBarRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const { data: searchResults, refetch } = useQuery({
    queryKey: ["search", searchTerm],
    queryFn: async () => {
      if (searchTerm.trim()) {
        const params = {
          searchString: searchTerm,
          searchTypes: ["MARKETPLACE"],
        } as SearchItemsParams
        const response = await neloApiClient.searchItems(params)
        return response.result
      }
      return []
    },
    enabled: false, // Don't run the query automatically
  })

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(() => {
      refetch()
    }, 500),
    [refetch]
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedSearch()
    setShowResults(true)
  }

  const handleInputFocus = () => {
    setIsFocused(true)
    if (searchTerm.trim() && searchResults && searchResults.length > 0) {
      setShowResults(true)
    }
  }

  const handleResultPress = (url: string) => {
    setShowResults(false)
    router.push(url)
  }

  const onSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.length === 0) {
        return
      }

      router.push(`/${countryCode}${buildRoute.search(searchTerm)}`)
    },
    [countryCode, router]
  )

  return (
    <div className="group relative flex w-full max-w-screen-sm items-center rounded-full bg-nelo-light-gray" ref={searchBarRef}>
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder="Busca electrónicos, moda y más"
        className="relative z-10 block w-full grow rounded-full border-2 border-transparent bg-transparent py-2 pl-4 pr-16 outline-none transition-colors duration-200 hover:border-nelo-blue focus:border-nelo-blue focus:bg-white active:border-nelo-blue active:bg-[#F0F9FF] group-hover:bg-[#F0F9FF]"
        onFocus={handleInputFocus}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSearch(searchTerm)
          }
        }}
        data-dd-privacy="allow"
      />
      <button
        onClick={() => onSearch(searchTerm)}
        className="absolute right-0 z-20 flex h-[30px] min-w-[30px] items-center justify-center rounded-full bg-nelo-blue transition-all duration-200 group-focus-within:h-full group-focus-within:min-w-[40px] group-hover:h-full group-hover:min-w-[40px]"
      >
        <BinocularsIcon className="text-[12px] transition-transform duration-200 group-focus-within:scale-[130%] group-hover:scale-[130%]" />
      </button>

      {showResults && searchResults && searchResults.length > 0 && (
        <div className="absolute left-0 top-[calc(100%+10px)] z-dropdown w-full">
          <SearchResults results={searchResults} onPress={handleResultPress}>
            <LocalizedClientLink
              href={buildRoute.search(searchTerm)}
              className="block w-full px-4 py-2 text-sm leading-[15px] text-nelo-blue"
              onClick={() => setShowResults(false)}
            >
              Ver todos los resultados
            </LocalizedClientLink>
          </SearchResults>
        </div>
      )}
    </div>
  )
}

export default WebSearchBar
